import { create } from 'zustand';
import { api, restoreSession, setAccessToken, setUnauthorizedHandler } from '@/lib/api/client';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  userType: 'STAFF' | 'TENANT';
  mfaEnabled: boolean;
  roles: string[];
  roleNames: string[];
  permissions: string[];
  lastLoginAt: string | null;
  malls: Array<{
    id: string;
    name: string;
    code: string;
    city: string;
    gla: number;
    unitCount: number;
  }>;
  canViewAllMalls: boolean;
}

interface AuthState {
  user: AuthUser | null;
  /** Açılışta sessiz yenileme denenirken true. */
  isBootstrapping: boolean;
  isLoggingIn: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  clearError: () => void;
}

/**
 * OTURUM DURUMU.
 *
 * ERİŞİM TOKEN'I BURADA SAKLANMAZ ve `persist` KULLANILMAZ.
 *
 * Token `lib/api/client` içinde yalnızca bellekte tutulur. localStorage'a
 * yazmak, herhangi bir XSS açığında token'ın okunabilmesi demektir.
 * Yenileme token'ı ise httpOnly çerezdedir ve JavaScript ondan haberdar
 * değildir — sayfa yenilendiğinde oturum, sessiz bir `POST /auth/refresh`
 * ile geri kazanılır.
 *
 * Bedeli: sekme açılışında bir ağ isteği. Karşılığı: çalınabilir bir
 * token'ın tarayıcıda hiç durmaması.
 */
/**
 * Açılıştaki kurtarma sözü — modül düzeyinde tutulur.
 *
 * Store içinde tutulsaydı, `set()` çağrısı bir render turu tetikler ve
 * ikinci çağrı sözü göremeden yola çıkabilirdi. Modül değişkeni render
 * döngüsünden bağımsızdır; yarış tamamen kapanır.
 */
let bootstrapPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  user: null,
  isBootstrapping: true,
  isLoggingIn: false,
  error: null,

  async login(email, password) {
    set({ isLoggingIn: true, error: null });

    try {
      const result = await api.post<{ accessToken: string }>(
        '/auth/login',
        { email, password },
        { skipMallScope: true, skipRefresh: true },
      );

      setAccessToken(result.accessToken);

      /*
        PROFİL AYRI BİR İSTEKLE ALINIR.

        Giriş yanıtındaki kullanıcı nesnesi ile `/auth/me` yanıtı farklı
        şekillerde döndüğü için, ekranlar hangi alanın dolu olduğunu
        tahmin etmek zorunda kalıyordu — nitekim `roles` alanı giriş
        yanıtında yoktu ve kullanıcı menüsü ilk girişte çöküyordu.

        Tek kaynak `/auth/me`'dir. Bir ağ isteği daha maliyeti karşılığında
        oturum durumu her yolda AYNI şekle sahip olur.
      */
      const user = await api.get<AuthUser>('/auth/me', { skipMallScope: true });

      set({ user, isLoggingIn: false });
      return true;
    } catch (error) {
      set({
        isLoggingIn: false,
        error:
          error instanceof Error
            ? // 401'i "sunucu hatası" gibi göstermemek önemli: kullanıcı
              // parolasını mı yanlış girdi, sistem mi bozuk — ayırt edebilmeli.
              (error as { status?: number }).status === 401
              ? 'E-posta veya parola hatalı.'
              : error.message
            : 'Giriş yapılamadı.',
      });
      return false;
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout', undefined, { skipMallScope: true, skipRefresh: true });
    } catch {
      /*
        Sunucuya ulaşılamasa bile YEREL OTURUM KAPATILIR.

        Kullanıcı "çıkış" dediğinde ekranın açık kalması kabul edilemez;
        özellikle paylaşılan bir bilgisayarda. Sunucudaki refresh token
        süresi dolduğunda zaten geçersizleşir.
      */
    } finally {
      setAccessToken(null);
      set({ user: null, error: null });
    }
  },

  /**
   * SAYFA AÇILIŞINDA SESSİZ OTURUM KURTARMA.
   *
   * İKİ KEZ ÇAĞRILSA BİLE AĞA TEK İSTEK ÇIKAR.
   *
   * Yenileme jetonu her kullanımda döner (rotation) ve sunucu, kullanılmış
   * bir jetonun tekrar gelmesini HIRSIZLIK sinyali sayıp kullanıcının tüm
   * oturum ailesini iptal eder — güvenlik açısından doğru davranış.
   *
   * Ama açılışta iki çağrı üst üste gelebiliyor (React StrictMode
   * geliştirmede efektleri iki kez çalıştırır). O zaman aynı jeton iki kez
   * gidiyor, ikinci istek hırsızlık sanılıyor ve kullanıcı sayfayı her
   * yenilediğinde dışarı atılıyordu.
   *
   * Çözüm iki katmanlı: yenileme isteği artık istemcinin TEK-UÇUŞ
   * kuyruğundan geçiyor (`restoreSession`), ve buradaki söz paylaşılıyor —
   * ikinci `bootstrap()` çağrısı birincinin sonucunu bekler.
   */
  async bootstrap() {
    if (bootstrapPromise) return bootstrapPromise;

    set({ isBootstrapping: true });

    bootstrapPromise = (async () => {
      try {
        const ok = await restoreSession();
        if (!ok) throw new Error('Oturum yenilenemedi');

        const user = await api.get<AuthUser>('/auth/me', { skipMallScope: true });
        set({ user, isBootstrapping: false });
      } catch {
        // Çerez yok veya süresi dolmuş — giriş ekranına düşülür.
        setAccessToken(null);
        set({ user: null, isBootstrapping: false });
      } finally {
        bootstrapPromise = null;
      }
    })();

    return bootstrapPromise;
  },

  /**
   * Yetki kontrolü.
   *
   * NEDEN BURADA DA KONTROL EDİLİYOR (SUNUCU ZATEN KONTROL EDİYOR):
   * Sunucu kontrolü GÜVENLİK içindir ve tek gerçek koruma odur. Buradaki
   * kontrol KULLANILABİLİRLİK içindir: kullanıcıya tıklayınca 403 alacağı
   * bir buton göstermemek. İkisi birbirinin yerine geçmez.
   */
  can(permission) {
    return get().user?.permissions.includes(permission) ?? false;
  },

  canAny(permissions) {
    if (permissions.length === 0) return true;
    const owned = get().user?.permissions ?? [];
    return permissions.some((p) => owned.includes(p));
  },

  clearError() {
    set({ error: null });
  },
}));

/*
  401 KÖPRÜSÜ.

  İstemci katmanı store'u tanımaz (tersi bağımlılık döngüsü olurdu).
  Yenileme başarısız olduğunda store'un haberdar olması için bir geri
  çağırma kaydediyoruz.
*/
setUnauthorizedHandler(() => {
  setAccessToken(null);
  useAuthStore.setState({ user: null });
});

/** Bileşenlerde kısa kullanım: `const can = usePermission();` */
export function usePermission(): (permission: string) => boolean {
  return useAuthStore((s) => s.can);
}
